import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { i18nReady } from './i18n';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { preloadHomeVariant, resolveHomeVariant } from './pages/Home/variants';
import { loadBranded } from './utils/bootstrap';
import { applyTypography, readStoredTypography } from './utils/typography';

// ── Server bootstrap seed ──────────────────────────────────────────────────
// The Express server injects fresh per-tenant branding into the HTML as
// `window.__HM_BRAND__`. To prevent every downstream consumer from having to
// know about the server payload, we seed localStorage with those values
// HERE — before any React component renders. After this line runs, every
// existing `localStorage.getItem('hm_theme_custom')` etc. just works.
//
// Cache invalidation: the server clears its tenant HTML cache whenever an
// admin saves, so this seed is also always fresh on every page load.
(function seedFromServer() {
    try {
        const b = (typeof window !== 'undefined') ? window.__HM_BRAND__ : null;
        if (!b || typeof b !== 'object') return;
        const writes = [
            ['hm_theme_custom',  b.theme],
            ['hm_hero_banner',   b.hero],
            ['hm_page_contact',  b.contact],
            ['hm_typography',    b.typography],
            ['hm_site_brand',    (b.siteName || b.logoUrl) ? { siteName: b.siteName || '', logoUrl: b.logoUrl || '' } : null],
        ];
        for (const [key, val] of writes) {
            if (val && typeof val === 'object') {
                try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* private mode */ }
            }
        }
    } catch { /* never let the seeder crash the app */ }
})();

// Site fonts (Appearance → Typography). The server already put the same
// <link>/<style> in the HTML, so this only matters where it couldn't (local
// dev against a remote API) and when an admin saves a new choice.
try {
    applyTypography(readStoredTypography());
    window.addEventListener('hm-settings-loaded', () => applyTypography(readStoredTypography()));
} catch { /* fonts are cosmetic, never block boot */ }

// Home page: fetch the HomeSwitch chunk AND the active theme's chunk now, in
// parallel with the rest of boot, instead of discovering the theme only after
// HomeSwitch has downloaded and rendered (one extra round trip before the hero).
if (typeof window !== 'undefined' && window.location.pathname === '/') {
    try {
        import('./pages/Home/HomeSwitch').catch(() => {});
        preloadHomeVariant(resolveHomeVariant(loadBranded('hm_hero_banner', 'hero', {})?.homeVariant))?.catch?.(() => {});
    } catch { /* preload only — the normal lazy path still works */ }
}

// Register service worker for PWA installability (enables beforeinstallprompt).
// Uses network-first strategy — never serves stale HTML or API responses.
if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
    if (process.env.NODE_ENV === 'production') {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
      return;
    }

    navigator.serviceWorker.getRegistrations()
      .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
      .catch(() => {});

    if ('caches' in window) {
      caches.keys()
        .then(keys => Promise.all(keys.map(key => caches.delete(key))))
        .catch(() => {});
    }
    });
}

// Disable browser's native scroll restoration so our own scrollTo(0,0)
// is never overridden asynchronously when navigating between routes.
// Without this, position:sticky cards appear broken on first SPA navigation.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

function isGTDomError(error) {
  const msg = (error && error.message) || '';
  return msg.includes('insertBefore') || msg.includes('removeChild');
}

class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false, error: null }; }
  static getDerivedStateFromError(error) {
    if (isGTDomError(error)) return { crashed: false, error: null };
    return { crashed: true, error };
  }
  componentDidCatch(error, info) {
    if (isGTDomError(error)) {
      const key = 'hm_gt_reload_count';
      const count = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (count < 3) { sessionStorage.setItem(key, String(count + 1)); window.location.reload(); }
      return;
    }
    console.error('[AppErrorBoundary] Caught:', error, info);
  }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', textAlign:'center', padding:'2rem', fontFamily:'sans-serif' }}>
          <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>⚠️</div>
          <h1 style={{ fontSize:'1.4rem', fontWeight:700, marginBottom:'0.5rem' }}>Something went wrong</h1>
          <p style={{ color:'#6b7280', marginBottom:'1.5rem', maxWidth:'400px' }}>The page encountered an unexpected error. Please refresh to try again.</p>
          <button onClick={() => window.location.reload()} style={{ background:'#10b981', color:'#fff', padding:'10px 28px', borderRadius:'8px', fontWeight:700, border:'none', cursor:'pointer', fontSize:'0.95rem' }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const renderApp = () => root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>
);
// Locales are lazy chunks now (perf: only English ships in main.js). Hold the
// very first render until the visitor's own language is in memory so
// non-English users never see an English flash — but never longer than 1.5s:
// if the locale chunk is slow/blocked, render anyway (English fallback) and
// the bundle re-renders everything when it lands.
Promise.race([i18nReady, new Promise((r) => setTimeout(r, 1500))]).then(renderApp);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
